import { NumberOrEmpty } from '@plato/ui-legacy/components/typography/number-or-empty';
import { PercentageOrEmpty } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import {
    InfoHint,
    InfoHintBody,
    InfoHintContent,
    InfoHintHeader,
    InfoHintLabel,
    InfoHintTitle,
    InfoHintTrigger,
} from '@plato/ui/components/info-hint';
import { ArrowUpRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { InfoHintStatDivider, InfoHintStatGrid, InfoHintStatRow } from './info-hint-stats.tsx';

export type ActivityCellProps = {
    // Headline: all logged interactions in the period.
    total: number;
    visits: number;
    phoneCalls: number;
    notes: number;
    visitsPerWeek: number;
    documentedVisits: number;
    plannedVisits: number;
    // Note substance: total notes authored and how many carry real content.
    notesTotal: number;
    notesSubstantive: number;
    // Deep-link to the rep's full activity feed; omitted when the feed is disabled.
    feedHref?: string;
    // Forwarded to the breakdown tooltip (analytics).
    onOpenChange?: (open: boolean) => void;
};

/**
 * "Customer Contacts" cell: one sortable total with the type breakdown, visit
 * cadence and note quality in a hover tooltip (skimmed version of the
 * manager-view prototype's ActivityCell — no trend chip / benchmark colours
 * until the prior-period MV data exists).
 */
export function ActivityCell({
    total,
    visits,
    phoneCalls,
    notes,
    visitsPerWeek,
    documentedVisits,
    plannedVisits,
    notesTotal,
    notesSubstantive,
    feedHref,
    onOpenChange,
}: ActivityCellProps) {
    const { t } = useTranslation('team');

    if (total === 0) {
        return (
            <div className="w-full text-right text-neutral-400">
                <NumberOrEmpty decimals={0}>{0}</NumberOrEmpty>
            </div>
        );
    }

    return (
        <div className="flex w-full items-center justify-end gap-1">
            <InfoHint onOpenChange={onOpenChange}>
                <InfoHintTrigger>
                    <InfoHintLabel>
                        <NumberOrEmpty decimals={0}>{total}</NumberOrEmpty>
                    </InfoHintLabel>
                </InfoHintTrigger>
                <InfoHintContent className="max-w-none" side="bottom">
                    <InfoHintHeader>
                        <InfoHintTitle>{t('user_table__column_contacts')}</InfoHintTitle>
                    </InfoHintHeader>
                    <InfoHintBody>
                        <InfoHintStatGrid>
                            <InfoHintStatRow
                                label={t('user_table__breakdown_total')}
                                value={total}
                                emphasized
                            />

                            <InfoHintStatDivider />

                            <InfoHintStatRow label={t('user_table__interaction_visits')} value={visits} />
                            <InfoHintStatRow
                                label={t('user_table__interaction_phone_calls')}
                                value={phoneCalls}
                            />
                            <InfoHintStatRow label={t('user_table__interaction_notes')} value={notes} />

                            <InfoHintStatDivider />

                            <InfoHintStatRow
                                label={t('user_table__visits_per_week')}
                                value={<NumberOrEmpty decimals={1}>{visitsPerWeek}</NumberOrEmpty>}
                            />
                            <InfoHintStatRow
                                label={t('user_table__visits_documented_planned')}
                                value={`${documentedVisits} / ${plannedVisits}`}
                            />

                            {notesTotal > 0 && (
                                <>
                                    <InfoHintStatDivider />
                                    <InfoHintStatRow
                                        label={t('user_table__note_depth')}
                                        value={
                                            <PercentageOrEmpty decimals={0}>
                                                {notesSubstantive / notesTotal}
                                            </PercentageOrEmpty>
                                        }
                                    />
                                </>
                            )}
                        </InfoHintStatGrid>
                    </InfoHintBody>
                </InfoHintContent>
            </InfoHint>
            {/* The feed link sits beside the hint: a hint explains, it never acts. */}
            {feedHref && (
                <Link
                    to={feedHref}
                    aria-label={t('user_table__open_activities')}
                    className="text-neutral-400 hover:text-neutral-700"
                >
                    <ArrowUpRightIcon className="h-3.5 w-3.5" />
                </Link>
            )}
        </div>
    );
}
